import type {
  CurrencyCode,
  DecimalValue,
  EvidenceReferenceV2,
  IsoDateTime,
  PortfolioAssetClassV2,
  PortfolioSupportState,
  PortfolioV2SchemaVersion,
  SourceStampV2,
} from "./primitives.js";
import type { StrategyObservationContextV2 } from "./strategy-observation.js";

export const TRADE_HORIZONS_V2 = ["SPECULATOR", "TRADER", "INVESTOR"] as const;
export type TradeHorizonV2 = typeof TRADE_HORIZONS_V2[number];
export const EXECUTION_FEE_STATES_V2 = ["REPORTED_AMOUNT", "REPORTED_ZERO", "UNKNOWN"] as const;
export type ExecutionFeeStateV2 = typeof EXECUTION_FEE_STATES_V2[number];
export const TRADE_PLAN_STATES_V2 = ["DRAFT", "VALIDATED", "READY_FOR_OWNER", "APPROVED", "EXECUTED", "REJECTED", "CANCELLED", "EXPIRED"] as const;
export type TradePlanStateV2 = typeof TRADE_PLAN_STATES_V2[number];
export const TRADE_LOT_STATES_V2 = ["OPEN", "PARTIALLY_CLOSED", "CLOSED", "CORRECTED"] as const;
export type TradeLotStateV2 = typeof TRADE_LOT_STATES_V2[number];
export const PLANNED_EXIT_QUANTITY_MODES_V2 = ["UNITS", "PERCENT_REMAINING", "PERCENT_ORIGINAL"] as const;
export const PLANNED_EXIT_TRIGGER_TYPES_V2 = ["PRICE_TARGET", "STOP", "THESIS", "TIME", "MANUAL_REVIEW"] as const;
export const PLANNED_EXIT_ACTIONS_V2 = ["NONE", "MOVE_STOP_TO_BREAKEVEN", "TRAIL_STOP", "REVIEW_REMAINDER"] as const;
export const PLANNED_EXIT_STATES_V2 = ["ACTIVE", "TRIGGERED", "MATCHED", "SUPERSEDED", "CANCELLED"] as const;
export const EXECUTION_SIDES_V2 = ["BUY", "SELL"] as const;
export const RISK_BINDING_CONSTRAINTS_V2 = ["RISK", "POSITION_CAP", "CASH", "LIQUIDITY"] as const;
export const FEE_COMPLETENESS_STATES_V2 = ["FINAL", "PROVISIONAL"] as const;
export const COUNTERFACTUAL_STATES_V2 = ["YES", "NO", "UNSURE"] as const;

export interface InstrumentReferenceV2 {
  instrumentId: string;
  symbol: string;
  name: string;
  market: string;
  currency: CurrencyCode;
  assetClass: PortfolioAssetClassV2;
  supportState: PortfolioSupportState;
}

export interface ResearchBindingV2 {
  bindingId: string;
  instrumentId: string;
  researchSessionId: string;
  researchRunId: string;
  snapshotId: string;
  technicalRef: string | null;
  fundamentalRef: string | null;
  valuationRef: string | null;
  externalConsensusRef: string | null;
  stamp: SourceStampV2;
  sealedAt: IsoDateTime;
}

export interface ExecutionCostsV2 {
  feeState: ExecutionFeeStateV2;
  commission: DecimalValue | null;
  regulatoryFees: DecimalValue | null;
  fxCosts: DecimalValue | null;
  otherCosts: DecimalValue | null;
}

export interface RiskCalculationV2 {
  formulaVersion: "portfolio-risk.v2.1";
  riskBase: DecimalValue;
  portfolioOpenRiskLimit: DecimalValue;
  tradeRiskLimit: DecimalValue;
  currentOpenRisk: DecimalValue;
  remainingOpenRisk: DecimalValue;
  availableNewTradeRisk: DecimalValue;
  riskPerUnit: DecimalValue;
  plannedRewardPerUnit: DecimalValue;
  rewardToRisk: DecimalValue;
  sharesByRisk: DecimalValue;
  sharesByPositionCap: DecimalValue;
  sharesByCash: DecimalValue;
  sharesByLiquidity: DecimalValue | null;
  proposedQuantity: DecimalValue;
  bindingConstraint: typeof RISK_BINDING_CONSTRAINTS_V2[number];
  inputFingerprint: string;
}

export interface TradePlanV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  tradePlanId: string;
  humanPlanRef: string;
  bookId: string;
  instrument: InstrumentReferenceV2;
  policyVersionId: string;
  researchBinding: ResearchBindingV2;
  horizon: TradeHorizonV2;
  state: TradePlanStateV2;
  plannedEntry: DecimalValue;
  plannedStop: DecimalValue;
  plannedTarget: DecimalValue;
  requestedTradeRiskPct: DecimalValue;
  estimatedEntryCostPerUnit: DecimalValue;
  estimatedExitCostPerUnit: DecimalValue;
  positionValueLimit: DecimalValue;
  availableCashAfterReserve: DecimalValue;
  liquidityQuantityCap: DecimalValue | null;
  risk: RiskCalculationV2;
  riskSnapshotId: string;
  thesis: string;
  invalidation: string;
  /** Null is read compatibility for plans persisted before P2.1; all new commands require an observation. */
  strategyObservation: StrategyObservationContextV2 | null;
  createdAt: IsoDateTime;
  approvedAt: IsoDateTime;
  expiresAt: IsoDateTime;
}

export interface PlannedExitRuleV2 {
  plannedExitId: string;
  tradePlanId: string;
  humanRef: string;
  sequence: number;
  quantityMode: typeof PLANNED_EXIT_QUANTITY_MODES_V2[number];
  quantityValue: DecimalValue;
  triggerType: typeof PLANNED_EXIT_TRIGGER_TYPES_V2[number];
  triggerValue: DecimalValue | string;
  postExecutionAction: typeof PLANNED_EXIT_ACTIONS_V2[number];
  rationale: string;
  state: typeof PLANNED_EXIT_STATES_V2[number];
  createdBeforeEntry: boolean;
  createdAt: IsoDateTime;
}

export interface TradeExecutionV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  executionId: string;
  executionGroupId: string;
  lotId: string;
  humanRef: string;
  side: typeof EXECUTION_SIDES_V2[number];
  quantity: DecimalValue;
  price: DecimalValue;
  currency: CurrencyCode;
  costs: ExecutionCostsV2;
  executedAt: IsoDateTime;
  recordedAt: IsoDateTime;
  brokerSourceId: string | null;
  brokerOrderRef: string | null;
  brokerExecutionRef: string | null;
  idempotencyKey: string;
  correctionOfExecutionId: string | null;
}

export interface TradeLotV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  lotId: string;
  humanLotRef: string;
  bookId: string;
  instrument: InstrumentReferenceV2;
  tradePlanId: string;
  policyVersionId: string;
  entryExecutionGroupId: string;
  openedAt: IsoDateTime;
  initialQuantity: DecimalValue;
  remainingQuantity: DecimalValue;
  weightedEntryPrice: DecimalValue;
  initialAcquisitionCosts: DecimalValue;
  allocatedOpenFeesRemaining: DecimalValue;
  adjustedUnitCost: DecimalValue;
  originalTarget: DecimalValue;
  currentTarget: DecimalValue;
  originalStop: DecimalValue;
  currentStop: DecimalValue;
  initialRisk: DecimalValue;
  remainingRisk: DecimalValue;
  initialRiskSnapshotId: string;
  remainingRiskSnapshotId: string;
  corporateActionRefs: string[];
  realizedGrossPnl: DecimalValue;
  realizedNetPnlBeforeTax: DecimalValue | null;
  feeCompleteness: typeof FEE_COMPLETENESS_STATES_V2[number];
  state: TradeLotStateV2;
  version: number;
}

export const SELL_REASONS_V2 = [
  "TARGET_REACHED",
  "STOP_REACHED",
  "THESIS_INVALIDATED",
  "NEW_EVENT",
  "PORTFOLIO_REBALANCE",
  "RISK_REDUCTION",
  "PLANNED_PARTIAL_EXIT",
  "PROFIT_PROTECTION",
  "FEAR_OR_DISCOMFORT",
  "OTHER_DOCUMENTED",
] as const;

export type SellReasonV2 = typeof SELL_REASONS_V2[number];

export interface ExitIntentV2 {
  exitIntentId: string;
  lotId: string;
  plannedExitId: string | null;
  requestedQuantity: DecimalValue;
  primaryReason: SellReasonV2;
  secondaryReasons: SellReasonV2[];
  note: string | null;
  evidence: EvidenceReferenceV2[];
  counterfactualWithoutPnl: typeof COUNTERFACTUAL_STATES_V2[number];
  capturedAt: IsoDateTime;
  lockedBeforeAgentAt: IsoDateTime;
  retroactive: boolean;
  correctionOfExitIntentId: string | null;
}

export const LEDGER_ACCOUNTS_V2 = ["CASH", "SECURITY_COST_BASIS", "REALIZED_PNL", "FEE_EXPENSE", "TAX_EXPENSE", "INCOME", "EXTERNAL_CAPITAL", "RECONCILIATION"] as const;
export type LedgerAccountV2 = typeof LEDGER_ACCOUNTS_V2[number];
export const LEDGER_TRANSACTION_STATES_V2 = ["FINAL", "PROVISIONAL_FEES_UNKNOWN"] as const;
export const LEDGER_EVENT_TYPES_V2 = ["BUY", "SELL", "DEPOSIT", "WITHDRAWAL", "DIVIDEND", "INTEREST", "TAX_WITHHELD", "FEE", "FX_CONVERSION", "RECONCILIATION_ADJUSTMENT", "REVERSAL", "CORRECTION"] as const;

export interface LedgerEntryV2 {
  account: LedgerAccountV2;
  debit: DecimalValue;
  credit: DecimalValue;
  currency: CurrencyCode;
}

export interface LedgerTransactionV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  ledgerTransactionId: string;
  bookId: string;
  eventType: typeof LEDGER_EVENT_TYPES_V2[number];
  executionId: string | null;
  occurredAt: IsoDateTime;
  knownAt: IsoDateTime;
  recordedAt: IsoDateTime;
  entries: LedgerEntryV2[];
  status: typeof LEDGER_TRANSACTION_STATES_V2[number];
  inputFingerprint: string;
  reversalOf: string | null;
  correctionReason: string | null;
}

export interface SellCalculationV2 {
  formulaVersion: "portfolio-ledger.v2.1";
  grossSaleProceeds: DecimalValue;
  disposalCosts: DecimalValue;
  netSaleProceeds: DecimalValue;
  allocatedEntryPrice: DecimalValue;
  allocatedBuyCosts: DecimalValue;
  allocatedAdjustedBasis: DecimalValue;
  grossPnl: DecimalValue;
  totalTradingCosts: DecimalValue;
  netPnlBeforeTax: DecimalValue | null;
  returnPctOnAdjustedBasis: DecimalValue | null;
  remainingQuantity: DecimalValue;
  remainingBuyCosts: DecimalValue;
  feeCompleteness: "FINAL" | "PROVISIONAL_FEES_UNKNOWN";
}
